import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";

dayjs.extend(quarterOfYear);

export type PresetRange = [string, string] | null;

export interface DatePreset {
  id: string;
  labelKey: string;
  future?: boolean;
  compute: () => PresetRange;
}

const fmt = (d: dayjs.Dayjs) => d.format("YYYY-MM-DD");

const startOfWeekMon = (d: dayjs.Dayjs) => d.subtract((d.day() + 6) % 7, "day");
const endOfWeekMon = (d: dayjs.Dayjs) => startOfWeekMon(d).add(6, "day");
const startOfQuarter = (d: dayjs.Dayjs) =>
  (d as any).startOf("quarter") as dayjs.Dayjs;
const endOfQuarter = (d: dayjs.Dayjs) =>
  (d as any).endOf("quarter") as dayjs.Dayjs;
const subQuarter = (d: dayjs.Dayjs, n: number) =>
  (d as any).subtract(n, "quarter") as dayjs.Dayjs;

// Quick column (relative to today).
export const BASE_COL_1: DatePreset[] = [
  {
    id: "today",
    labelKey: "today",
    compute: () => [fmt(dayjs()), fmt(dayjs())],
  },
  {
    id: "yesterday",
    labelKey: "yesterday",
    compute: () => [
      fmt(dayjs().subtract(1, "day")),
      fmt(dayjs().subtract(1, "day")),
    ],
  },
  {
    id: "last_7_days",
    labelKey: "last_7_days",
    compute: () => [fmt(dayjs().subtract(6, "day")), fmt(dayjs())],
  },
  {
    id: "last_30_days",
    labelKey: "last_30_days",
    compute: () => [fmt(dayjs().subtract(29, "day")), fmt(dayjs())],
  },
  {
    id: "last_90_days",
    labelKey: "last_90_days",
    compute: () => [fmt(dayjs().subtract(89, "day")), fmt(dayjs())],
  },
  {
    id: "last_180_days",
    labelKey: "last_180_days",
    compute: () => [fmt(dayjs().subtract(179, "day")), fmt(dayjs())],
  },
  {
    id: "year_to_date",
    labelKey: "year_to_date",
    compute: () => [fmt(dayjs().startOf("year")), fmt(dayjs())],
  },
];

// Calendar column (fixed calendar periods).
export const BASE_COL_2: DatePreset[] = [
  {
    id: "this_week",
    labelKey: "this_week",
    compute: () => [fmt(startOfWeekMon(dayjs())), fmt(endOfWeekMon(dayjs()))],
  },
  {
    id: "last_week",
    labelKey: "last_week",
    compute: () => [
      fmt(startOfWeekMon(dayjs().subtract(1, "week"))),
      fmt(endOfWeekMon(dayjs().subtract(1, "week"))),
    ],
  },
  {
    id: "this_month",
    labelKey: "this_month",
    compute: () => [fmt(dayjs().startOf("month")), fmt(dayjs().endOf("month"))],
  },
  {
    id: "last_month",
    labelKey: "last_month",
    compute: () => [
      fmt(dayjs().subtract(1, "month").startOf("month")),
      fmt(dayjs().subtract(1, "month").endOf("month")),
    ],
  },
  {
    id: "this_quarter",
    labelKey: "this_quarter",
    compute: () => [fmt(startOfQuarter(dayjs())), fmt(endOfQuarter(dayjs()))],
  },
  {
    id: "last_quarter",
    labelKey: "last_quarter",
    compute: () => [
      fmt(startOfQuarter(subQuarter(dayjs(), 1))),
      fmt(endOfQuarter(subQuarter(dayjs(), 1))),
    ],
  },
  {
    id: "this_year",
    labelKey: "this_year",
    compute: () => [fmt(dayjs().startOf("year")), fmt(dayjs().endOf("year"))],
  },
  {
    id: "last_year",
    labelKey: "last_year",
    compute: () => [
      fmt(dayjs().subtract(1, "year").startOf("year")),
      fmt(dayjs().subtract(1, "year").endOf("year")),
    ],
  },
];

export const PRESET_TOMORROW: DatePreset = {
  id: "tomorrow",
  labelKey: "tomorrow",
  future: true,
  compute: () => [fmt(dayjs().add(1, "day")), fmt(dayjs().add(1, "day"))],
};

export const PRESET_NEXT_7_DAYS: DatePreset = {
  id: "next_7_days",
  labelKey: "next_7_days",
  future: true,
  compute: () => [fmt(dayjs()), fmt(dayjs().add(6, "day"))],
};

export const PRESET_NEXT_30_DAYS: DatePreset = {
  id: "next_30_days",
  labelKey: "next_30_days",
  future: true,
  compute: () => [fmt(dayjs()), fmt(dayjs().add(29, "day"))],
};

// Quick column when forward-looking presets are enabled (Appointments, Tasks).
export const FUTURE_COL_1: DatePreset[] = [
  PRESET_TOMORROW,
  BASE_COL_1[0], // today
  BASE_COL_1[1], // yesterday
  PRESET_NEXT_7_DAYS,
  PRESET_NEXT_30_DAYS,
  ...BASE_COL_1.slice(2),
];

function buildPresetComputeMap(): Record<string, () => [string, string]> {
  const presets = [
    ...BASE_COL_1,
    ...BASE_COL_2,
    PRESET_TOMORROW,
    PRESET_NEXT_7_DAYS,
    PRESET_NEXT_30_DAYS,
  ];
  const map: Record<string, () => [string, string]> = {};
  for (const preset of presets) {
    map[preset.id] = () => {
      const range = preset.compute();
      if (!range) {
        throw new Error(`Preset "${preset.id}" returned null`);
      }
      return range;
    };
  }
  return map;
}

export const PRESET_COMPUTE = buildPresetComputeMap();
